import React, { ReactNode } from 'react';
import { I18n } from 'react-redux-i18n';
import { Form } from 'react-final-form';
import { FORM_ERROR } from 'final-form';
import { Entity, TypeEntityEnum } from '../../../../../network/api/types/compte/Entity';
import compteApi from '../../../../../network/api/compteApi';
import StructureGeneralInformation from './StructureGeneralInformation';
import StructureContact from './StructureContact';
import StructureAccessibility from './StructureAccessibility';
import StructureAddressGeo from './StructureAddressGeo';
import StructureAddressPostal from './StructureAddressPostal';
import StructureOpeningHours from './StructureOpeningHours';
import { OpeningHours } from '../../../../../network/api/types/compte/OpeningHours';
import { Day, DayOfWeekEnum } from '../../../../../network/api/types/compte/Day';
import { LocationDTO } from '../../../../../network/api/types/compte/LocationDTO';
import Button from '../../../../common/Button';
import { notifyErrorWithMessage, notifySuccess } from '../../../../common/Notification';
import { TimeSlot } from '../../../../../network/api/types/compte/TimeSlot';
import { FieldErrorDTO } from '../../../../../network/api/types/compte/FieldErrorDTO';
import { ErrorDTO } from '../../../../../network/api/types/compte/ErrorDTO';
import ConfirmationModal from '../../../../common/modals/ConfirmationModal';

type Props = {
  onClose: () => void
  setDirty?: (dirty: boolean, callback?: () => void) => void
  location: LocationDTO
  entity: Entity
  isManager: boolean
};

type State = {
  openingHours: OpeningHours
  hoursDirty: boolean
  confirmModalOpen: boolean
  isLoading: boolean
};

type FormValues = {
  phoneNumber?: string,
  email?: string,
  logo?: string
  siteInternet?: string
  clientParking?: boolean
  elevator?: boolean
  handicapAccess?: boolean
  ctmAdrGeo4?: string
  ctmAdrGeo5?: string
  ctmAdrGeoCodePostal?: string
  ctmAdrGeoVille?: string
  ctmAdrPostal1?: string
  ctmAdrPostal2?: string
  ctmAdrPostal3?: string
  ctmAdrPostal4?: string
  ctmAdrPostal5?: string
  ctmAdrPostalCodePostal?: string
  ctmAdrPostalVille?: string
};

let initialValues: FormValues = {};

let fetchedLocation: LocationDTO = {};

const newDayOpeningHours: TimeSlot = {
  startH: 8,
  startM: 30,
  finishH: 17,
  finishM: 30,
};

class StructureInformation extends React.Component<Props, State> {
  state: State = {
    openingHours: null,
    hoursDirty: false,
    confirmModalOpen: false,
    isLoading: false,
  };

  componentDidMount(): void {
    this.setInitialValues();
  }

  shouldComponentUpdate(nextProps: Readonly<Props>): boolean {
    if (this.props.location !== nextProps.location) {
      this.setInitialValues(nextProps.location);
      return true;
    }
    return true;
  }

  setInitialValues = (location?: LocationDTO): void => {
    fetchedLocation = location || this.props.location;
    initialValues = {
      ...initialValues,
      phoneNumber: fetchedLocation?.phoneNumber,
      email: fetchedLocation?.email,
      ctmAdrGeo4: fetchedLocation?.geoAddress.addr4,
      ctmAdrGeo5: fetchedLocation?.geoAddress.addr5,
      ctmAdrGeoCodePostal: fetchedLocation?.geoAddress.codePostal,
      ctmAdrGeoVille: fetchedLocation?.geoAddress.ville,
      ctmAdrPostal1: fetchedLocation?.postalAddress.addr1,
      ctmAdrPostal2: fetchedLocation?.postalAddress.addr2,
      ctmAdrPostal3: fetchedLocation?.postalAddress.addr3,
      ctmAdrPostal4: fetchedLocation?.postalAddress.addr4,
      ctmAdrPostal5: fetchedLocation?.postalAddress.addr5,
      ctmAdrPostalCodePostal: fetchedLocation?.postalAddress.codePostal,
      ctmAdrPostalVille: fetchedLocation?.postalAddress.ville,
      handicapAccess: fetchedLocation?.handicapAccess,
      clientParking: fetchedLocation?.clientParking,
      elevator: fetchedLocation?.elevator,
      siteInternet: this.props.entity.siteInternet,
    };
    if (fetchedLocation) {
      this.setHours(fetchedLocation);
    }
  };

  setHours = (location: LocationDTO): void => {
    this.setState(() => {
      const newHours = location.openingHours;
      // cas où days = null
      if (!newHours.days) {
        newHours.days = [];
      }
      // gestion des jours fermés
      const dayStrings = location.openingHours.days.map((day) => day.dayOfWeek);
      Object.values(DayOfWeekEnum)
        .filter((dayString) => !dayStrings.includes(dayString))
        .map((dayString) => newHours.days.push({
          dayOfWeek: dayString,
          openingHours: [],
        }));
      newHours.days?.forEach((day) => {
        if (!day.openingHours.length) {
          day.openingHours.push(newDayOpeningHours);
          initialValues[`closed${day.dayOfWeek}`] = true;
        } else {
          initialValues[`closed${day.dayOfWeek}`] = false;
        }
      });
      return {
        openingHours: newHours,
      };
    });
  };

  onFormChange = (dirty: boolean): void => {
    const setDirty = this.state.hoursDirty || dirty;
    this.props.setDirty(setDirty);
  };

  removeHours = (dayIndex: number, hoursIndex: number): void => {
    this.setState((prevState) => {
      const days = prevState.openingHours.days;
      return {
        openingHours: {
          days: days.map((day, dayIndexState) => {
            if (dayIndexState === dayIndex) {
              return {
                dayOfWeek: day.dayOfWeek,
                openingHours: day.openingHours
                  .filter((hours, hoursIndexState) => hoursIndexState !== hoursIndex),
              };
            }
            return day;
          }),
        },
        hoursDirty: true,
      };
    }, () => this.props.setDirty(true));
  };

  changeHours = (
    dayIndex: number, hoursIndex: number,
    startH?: number,
    startM?: number,
    finishH?: number,
    finishM?: number,
  ): void => {
    this.setState((prevState) => {
      const days = prevState.openingHours.days;
      return {
        openingHours: {
          days: days.map((day, dayIndexState) => {
            if (dayIndexState === dayIndex) {
              return {
                dayOfWeek: day.dayOfWeek,
                openingHours: hoursIndex === day.openingHours.length
                  ? day.openingHours.concat({
                    startH,
                    startM,
                    finishH,
                    finishM,
                  })
                  : day.openingHours.map((hours, hoursIndexState) => {
                    if (hoursIndexState === hoursIndex) {
                      return (startH === null) ? {
                        ...hours,
                        finishH,
                        finishM,
                      } : {
                        ...hours,
                        startH,
                        startM,
                      };
                    }
                    return hours;
                  }),
              };
            }
            return day;
          }),
        },
        hoursDirty: true,
      };
    }, () => this.props.setDirty(true));
  };

  getValueIfChanged = (formValue: string, key: string, key2?: string): string => {
    if (key2) {
      if (fetchedLocation[key] && (formValue === fetchedLocation[key][key2]
          || (!formValue && !fetchedLocation[key][key2]))) {
        return null;
      }
    } else if (formValue === fetchedLocation[key]
        || (!formValue && !fetchedLocation[key])) {
      return null;
    }
    return formValue || '';
  };

  setLoading = (loading: boolean): void => {
    this.setState(() => ({ isLoading: loading }));
  };

  saveValues = async (values: FormValues): Promise<{ [FORM_ERROR]: FieldErrorDTO[] }> => {
    this.setLoading(true);
    try {
      const newDays: Day[] = [];
      this.state.openingHours.days.forEach((day) => {
        if (!values[`closed${day.dayOfWeek}`]) {
          newDays.push({
            dayOfWeek: day.dayOfWeek,
            openingHours: day.openingHours,
          });
        } else {
          newDays.push({
            dayOfWeek: day.dayOfWeek,
            openingHours: [],
          });
        }
      });
      fetchedLocation = {
        ...fetchedLocation,
        // ou: null,
        clientParking: values.clientParking,
        elevator: values.elevator,
        handicapAccess: values.handicapAccess,
        email: this.getValueIfChanged(values.email, 'email'),
        geoAddress: {
          ...fetchedLocation.geoAddress,
          addr4: this.getValueIfChanged(values.ctmAdrGeo4, 'geoAddress', 'addr4'),
          addr5: this.getValueIfChanged(values.ctmAdrGeo5, 'geoAddress', 'addr5'),
          ville: this.getValueIfChanged(values.ctmAdrGeoVille, 'geoAddress', 'ville'),
          codePostal: this.getValueIfChanged(values.ctmAdrGeoCodePostal, 'geoAddress', 'codePostal'),
        },
        postalAddress: {
          ...fetchedLocation.postalAddress,
          addr1: this.getValueIfChanged(values.ctmAdrPostal1, 'postalAddress', 'addr1'),
          addr2: this.getValueIfChanged(values.ctmAdrPostal2, 'postalAddress', 'addr2'),
          addr3: this.getValueIfChanged(values.ctmAdrPostal3, 'postalAddress', 'addr3'),
          addr4: this.getValueIfChanged(values.ctmAdrPostal4, 'postalAddress', 'addr4'),
          addr5: this.getValueIfChanged(values.ctmAdrPostal5, 'postalAddress', 'addr5'),
          ville: this.getValueIfChanged(values.ctmAdrPostalVille, 'postalAddress', 'ville'),
          codePostal: this.getValueIfChanged(values.ctmAdrPostalCodePostal, 'postalAddress', 'codePostal'),
        },
        phoneNumber: this.getValueIfChanged(values.phoneNumber, 'phoneNumber'),
        openingHours: {
          days: newDays,
        },
        ouEntity: this.props.entity.id,
      };

      const promises: Promise<{ [FORM_ERROR]: FieldErrorDTO[] }>[] = [];
      promises.push(compteApi.modifyEntite({
        url: values.siteInternet || '',
        id: this.props.entity.id,
        img: values.logo !== null ? values.logo : '',
      })
        .catch((err) => {
          if (typeof err.then === 'function') {
            return err.then((e: ErrorDTO) => ({ [FORM_ERROR]: e.fieldErrors || e.message }));
          }
          return null;
        }));
      promises.push(compteApi.updateLocation(fetchedLocation)
        .then((location: LocationDTO) => {
          fetchedLocation = location;
        })
        .catch((err) => {
          if (typeof err.then === 'function') {
            return err.then((e: ErrorDTO) => ({ [FORM_ERROR]: e.fieldErrors || e.message }));
          }
          return null;
        }));
      let retErrors: FieldErrorDTO[] = [];
      return Promise.all(promises)
        .then((submitErrors) => {
          submitErrors.forEach((err) => {
            if (err && err[FORM_ERROR]) {
              retErrors = retErrors.concat(err[FORM_ERROR]);
            }
          });
          if (!retErrors.length) {
            this.setHours(fetchedLocation);
            this.props.setDirty(false);
            this.props.onClose();
            notifySuccess(I18n.t('STRUCTURE_INFORMATION.UPDATE_SUCCESS'));
          } else {
            notifyErrorWithMessage(I18n.t('message.ERROR_MODIF'));
          }
          return { [FORM_ERROR]: retErrors };
        });
    } catch (err) {
      this.setLoading(false);
      return { [FORM_ERROR]: [] };
    }
  };

  handleEdit = async (values: FormValues): Promise<{ [FORM_ERROR]: FieldErrorDTO[] }> => {
    if (this.state.confirmModalOpen || (
      this.props.entity.typeEntite !== TypeEntityEnum.OFFICE
        && this.props.entity.typeEntite !== TypeEntityEnum.INSTANCE
    )) {
      return this.saveValues(values)
        .finally(() => this.setState(() => ({ confirmModalOpen: false, isLoading: false })));
    }
    this.setState(() => ({ confirmModalOpen: true }));
    return null;
  };

  openModal = (): void => {
    this.setState((prevState) => ({ confirmModalOpen: !prevState.confirmModalOpen }));
  };

  isAddressReadonly = (): boolean => TypeEntityEnum.INSTANCE === this.props.entity.typeEntite
    || TypeEntityEnum.OFFICE === this.props.entity.typeEntite
    || !this.props.isManager;

  render(): ReactNode {
    return (
      <div className="structure-drawer-content">
        <Form
          onSubmit={this.handleEdit}
          initialValues={initialValues}
          render={({
            handleSubmit,
            form,
            submitError,
          }) => {
            const getErrorMessage = (fieldName: string): string => {
              if (submitError) {
                return submitError.find(
                  (error: FieldErrorDTO) => error.field === fieldName,
                )?.message;
              }
              return '';
            };
            const checkErrors = (): void => {
              const { errors } = form.getState();
              if ((errors && Object.keys(errors).length)
                      || (submitError && Object.keys(submitError).length)) {
                const firstErrorName = (errors && Object.keys(errors)[0])
                        || (submitError && Object.keys(submitError)[0]);
                const firstErrorField: HTMLInputElement = document.querySelector(`#${firstErrorName}`);
                firstErrorField.parentElement.parentElement.parentElement.parentElement
                  .scrollIntoView({ behavior: 'smooth' });
                firstErrorField.focus({ preventScroll: true });
              }
            };
            return (
              <form
                className="structure-information-form"
                onSubmit={(this.state.hoursDirty || form.getState().dirty
                  ? (e) => {
                    const result = handleSubmit(e);
                    if (result && typeof result.then === 'function') {
                      return result.then(checkErrors, () => form.getState().submitErrors);
                    }
                    checkErrors();
                    return form.getState().errors;
                  }
                  : this.props.onClose)}
                onChange={() =>
                  this.onFormChange(form.getState().dirty)}
              >
                <ConfirmationModal
                  isOpen={this.state.confirmModalOpen}
                  onClose={() => this.openModal()}
                  titleI18nKey="PROFESSIONAL_INFORMATION.CONFIRM_SUBMIT_TITLE"
                  subtTitleI18nKey="PROFESSIONAL_INFORMATION.CONFIRM_SUBMIT_SUBTITLE"
                  onConfirm={form.submit}
                  onCancel={() => this.openModal()}
                  confirmeDisabled={this.state.isLoading}
                  isLoading={this.state.isLoading}
                />
                <div className="structure-form-scrollable">
                  <StructureGeneralInformation entity={this.props.entity} />
                  <div className="drawer-divider-horizontal" />
                  <StructureContact
                    logo={this.props.entity.logo}
                    getDisplayedError={getErrorMessage}
                    isManager={this.props.isManager}
                  />
                  <div className="drawer-divider-horizontal" />
                  <StructureAccessibility
                    defaultChecked={['clientParking', 'elevator', 'handicapAccess'].map((key) => initialValues[key])}
                    isManager={this.props.isManager}
                  />
                  <div className="drawer-divider-horizontal" />
                  <StructureAddressGeo
                    getDisplayedError={getErrorMessage}
                    readonly={this.isAddressReadonly()}
                  />
                  <div className="drawer-divider-horizontal" />
                  <StructureAddressPostal
                    getDisplayedError={getErrorMessage}
                    readonly={this.isAddressReadonly()}
                  />
                  <div className="drawer-divider-horizontal" />
                  <StructureOpeningHours
                    openingHours={this.state.openingHours}
                    changeHours={this.changeHours}
                    removeHours={this.removeHours}
                    defaultClosed={Object.values(DayOfWeekEnum).map((dayOfWeek) => initialValues[`closed${dayOfWeek}`])}
                    isManager={this.props.isManager}
                  />
                </div>
                {this.props.isManager
                      && (
                      <div className="drawer-button-footer">
                        <div className="structure-illustration" />
                        <div className="button-container">
                          <Button
                            type="third"
                            onClick={this.props.onClose}
                            i18nKey="actions.CANCEL"
                          />
                          <Button
                            type="primary"
                            buttonType="submit"
                            i18nKey="actions.SAVE"
                            isLoading={this.state.isLoading}
                            disabled={this.state.isLoading}
                          />
                          <div className="structure-illustration" />
                        </div>
                      </div>
                      )}
              </form>
            );
          }}
        />
      </div>
    );
  }
}

export default StructureInformation;
